import update, { Spec } from 'immutability-helper'
import { StoreActionMap } from '@/store/types'
import { HydraCollection, IRI } from 'emt-library'
import { Conversation } from '@/dataV3/Conversation/Conversation'
import { ConversationMessage } from '@/dataV3/Conversation/ConversationMessage'
import { ConversationSummary } from '@/dataV3/Conversation/ConversationSummary'
import { ConversationNotification } from '@/dataV3/Conversation/ConversationNotification'

export interface DiscussionsReducer {
  totalCount?: number
  currentSummaries?: ConversationSummary[]
  currentDiscussions?: Record<
    string, // Indexed by discussionId
    Conversation
  >
  mercureIRI?: IRI | null | undefined
  nextPage?: string | null
  unreadCount?: number
  __version?: number
}

const CURRENT_VERSION = 2

export const initialDiscussionsReducerState: DiscussionsReducer = {
  totalCount: 0,
  currentSummaries: [],
  currentDiscussions: {},
  nextPage: null,
  mercureIRI: null,
  unreadCount: 0,
  __version: CURRENT_VERSION,
}

export enum DiscussionsTypes {
  Restore = 'Discussions/RESTORE',
  UnreadCountUpdated = 'Discussions/UNREAD_COUNT_UPDATED',
  DiscussionsUpdated = 'Discussions/DISCUSSIONS_UPDATED',
  DiscussionUpdated = 'Discussions/DISCUSSION_UPDATED',
  DiscussionSummaryUpdated = 'Discussions/DISCUSSION_SUMMARY_UPDATED',
  DiscussionMessagesUpdated = 'Discussions/DISCUSSION_MESSAGES_UPDATED',
  DiscussionMessageReceived = 'Discussions/DISCUSSION_MESSAGE_RECEIVED',
  DiscussionDeleted = 'Discussions/DISCUSSION_DELETED',
  Clean = 'Discussions/CLEAN',
}

type DiscussionsPayload = {
  [DiscussionsTypes.Restore]: DiscussionsReducer
  [DiscussionsTypes.UnreadCountUpdated]: number
  [DiscussionsTypes.DiscussionsUpdated]: {
    isFirstPage: boolean
    discussionsCount: number
    discussions: ConversationSummary[]
    nextPage: IRI | null | undefined
    mercureIRI: IRI | null | undefined
  }
  [DiscussionsTypes.DiscussionUpdated]: { conversation: Conversation }
  [DiscussionsTypes.DiscussionSummaryUpdated]: { summary: ConversationSummary }
  [DiscussionsTypes.DiscussionMessagesUpdated]: {
    discussionUuid: string
  } & HydraCollection<ConversationMessage | ConversationNotification>
  [DiscussionsTypes.DiscussionMessageReceived]: {
    discussionUuid: string
    message: ConversationMessage
  }
  [DiscussionsTypes.DiscussionDeleted]: string
  [DiscussionsTypes.Clean]: null
}

export type DiscussionsActions = StoreActionMap<DiscussionsPayload>[keyof StoreActionMap<DiscussionsPayload>]

const discussionsReducer = (curState: DiscussionsReducer, action: DiscussionsActions): DiscussionsReducer => {
  switch (action.type) {
    case DiscussionsTypes.Restore: {
      if (action.payload.__version === CURRENT_VERSION) {
        return {
          ...action.payload,
        }
      }
      return { ...initialDiscussionsReducerState, ...action.payload, __version: CURRENT_VERSION }
    }
    case DiscussionsTypes.UnreadCountUpdated: {
      return { ...curState, unreadCount: action.payload }
    }
    case DiscussionsTypes.DiscussionsUpdated: {
      const { isFirstPage, discussionsCount, discussions, nextPage, mercureIRI } = action.payload

      const currentDiscussionsIds = curState.currentSummaries?.map((d) => d.conversationUuid) || []
      const newDiscussions = !isFirstPage
        ? discussions.filter((d) => {
            if (!currentDiscussionsIds.includes(d.conversationUuid)) {
              return d
            }
          })
        : []

      const stateUpdate: Spec<DiscussionsReducer> = {
        totalCount: { $set: discussionsCount },
        nextPage: { $set: nextPage || null },
        mercureIRI: { $set: mercureIRI || null },
        // Reset or push new discussions
        currentSummaries: isFirstPage ? { $set: discussions } : { $push: newDiscussions },
      }

      return update(curState, stateUpdate)
    }
    case DiscussionsTypes.DiscussionUpdated: {
      const { conversation } = action.payload
      const stateUpdate: Spec<DiscussionsReducer> = {
        currentDiscussions: {
          [conversation.uuid]: { $set: conversation },
        },
      }

      return update(curState, stateUpdate)
    }
    case DiscussionsTypes.DiscussionSummaryUpdated: {
      const { summary } = action.payload

      const index =
        curState.currentSummaries?.findIndex((it) => {
          return it.conversationUuid === summary.conversationUuid
        }) || -1

      let finalState = curState
      if (index >= 0) {
        // Remove summary if exists
        const stateUpdate: Spec<DiscussionsReducer> = {
          currentSummaries: { $splice: [[index, 1]] },
        }

        finalState = update(finalState, stateUpdate)
      }

      // Add new summary at top
      const finalStateUpdate: Spec<DiscussionsReducer> = {
        currentSummaries: { $splice: [[0, 0, summary]] },
      }
      if (index < 0) {
        finalStateUpdate.totalCount = {
          $apply: function (x = 0) {
            return x + 1
          },
        }
      }
      return update(finalState, finalStateUpdate)
    }
    case DiscussionsTypes.DiscussionMessagesUpdated: {
      const {
        discussionUuid,
        'hydra:member': messages,
        'hydra:totalItems': messagesCount,
        'hydra:view': view,
      } = action.payload

      const stateUpdate: Spec<DiscussionsReducer> = {
        currentDiscussions: {
          [discussionUuid]: {
            conversationItems: {
              'hydra:totalItems': { $set: messagesCount },
              'hydra:member': { $push: messages },
              'hydra:view': { $set: view },
            },
          },
        },
      }
      return update(curState, stateUpdate)
    }
    case DiscussionsTypes.DiscussionMessageReceived: {
      const { discussionUuid, message } = action.payload

      const stateUpdate: Spec<DiscussionsReducer> = {
        currentDiscussions: {
          [discussionUuid]: {
            conversationItems: {
              'hydra:totalItems': {
                $apply: function (x) {
                  return x + 1
                },
              },
              'hydra:member': { $splice: [[0, 0, message]] },
            },
          },
        },
      }

      return update(curState, stateUpdate)
    }
    case DiscussionsTypes.DiscussionDeleted: {
      const conversationUuid = action.payload
      const index =
        curState.currentSummaries?.findIndex((summary) => {
          return summary.conversationUuid === conversationUuid
        }) || -1
      const updatedState: Spec<DiscussionsReducer> = {}
      if (index >= 0) {
        // Discussion exists
        updatedState.currentSummaries = { $splice: [[index, 1]] }
        updatedState.totalCount = {
          $apply: function (x = 0) {
            return x - 1
          },
        }
        updatedState.currentDiscussions = { $unset: [conversationUuid] }
      } else {
        // Discussion does not exist, do nothing
      }

      return update(curState, updatedState)
    }
    case DiscussionsTypes.Clean: {
      return initialDiscussionsReducerState
    }
    default: {
      return curState
    }
  }
}

export default discussionsReducer
